import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api/client';
import { url } from '../../api';
import { toast } from 'react-toastify';
import './CoffeeAdmin.css';
import IconSelector from './IconSelector';
import { fetchIcon } from '../../utils/icons';
import { getAuthToken } from '../../utils/authTokens';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faEdit,
    faTrash,
    faSave,
    faTimes,
    faEye,
    faEyeSlash,
    faToggleOff,
    faCopy,
    faMugHot,
    faIceCream,
    faCow,
    faCube,
    faSearch
} from '@fortawesome/free-solid-svg-icons';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { useConfirm } from '../../ui/components/ConfirmDialog';
import { coffeeAdminPaths } from './api/paths';

interface CoffeeType {
    _id: string;
    coffeeKey: string;
    fullName: string;
    displayName: string;
    iconName: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
    coffeeFormat: any;
    rateCategories: any;
}

interface AvailableIcon {
    name: string;
    url: string;
    displayName: string;
}

interface CoffeeFormData {
    coffeeKey: string;
    fullName: string;
    iconName: string;
    isActive: boolean;
    coffeeFormat: {
        sugar?: {
            level?: string[];
            type?: string[];
        };
        milk?: {
            type?: string[];
        };
        type?: string[];
        'ice-cream'?: {
            type?: string[];
        };
    };
    rateCategories: {
        [key: string]: {
            weight: number;
            required: boolean;
        };
    };
}

const CoffeeAdmin: React.FC = () => {
    const confirm = useConfirm();
    const [coffeeTypes, setCoffeeTypes] = useState<CoffeeType[]>([]);
    const [availableIcons, setAvailableIcons] = useState<AvailableIcon[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [iconUrls, setIconUrls] = useState<{ [key: string]: string }>({});
    const [search, setSearch] = useState('');
    const [chipInputs, setChipInputs] = useState<Record<string, string>>({});
    const [formData, setFormData] = useState<CoffeeFormData>({
        coffeeKey: '',
        fullName: '',
        iconName: '',
        isActive: true,
        coffeeFormat: {},
        rateCategories: {}
    });

    // default coffee format options
    const defaultFormatOptions = {
        sugar: {
            level: ['no-sugar', 'a-little', 'medium', 'a-lot'],
            type: ['white', 'brown']
        },
        milk: {
            type: ['no-milk', 'plant', 'cow', 'coconut', 'almond', 'oat']
        }
    };

    // default rate categories
    const defaultRateCategories = {
        body: { weight: 10, required: true },
        flavour: { weight: 7, required: true },
        acidity: { weight: 6, required: true },
        aftertaste: { weight: 6, required: true },
        look: { weight: 4, required: true }
    };

    useEffect(() => {
        fetchCoffeeTypes();
        fetchAvailableIcons();
    }, []);

    const fetchCoffeeTypes = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get(`${url}${coffeeAdminPaths.coffees()}`, {
                headers: { Authorization: `Bearer ${getAuthToken()}` }
            });

            if (response.data.success) {
                setCoffeeTypes(response.data.data);

                // fetch and cache icon URLs
                const urlMap: { [key: string]: string } = {};
                for (const coffee of response.data.data) {
                    try {
                        const iconUrl = await fetchIcon(coffee.iconName);
                        if (iconUrl) {
                            urlMap[coffee.iconName] = iconUrl;
                        }
                    } catch (error) {
                        console.error(`Failed to fetch icon for ${coffee.iconName}:`, error);
                    }
                }
                setIconUrls(urlMap);
            }
        } catch (error) {
            console.error('Error fetching coffee types:', error);
            toast.error('Failed to fetch coffee types');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableIcons = async () => {
        try {
            const response = await apiClient.get(`${url}${coffeeAdminPaths.icons()}`, {
                headers: { Authorization: `Bearer ${getAuthToken()}` }
            });

            if (response.data.success) {
                setAvailableIcons(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching available icons:', error);
            toast.error('Failed to fetch available icons');
        }
    };

    const handleEdit = (coffee: CoffeeType) => {
        setFormData({
            coffeeKey: coffee.coffeeKey,
            fullName: coffee.fullName,
            iconName: coffee.iconName,
            isActive: coffee.isActive,
            coffeeFormat: coffee.coffeeFormat || defaultFormatOptions,
            rateCategories: coffee.rateCategories || defaultRateCategories
        });
        setEditingId(coffee._id);
        setShowAddForm(false);
    };

    const handleAdd = () => {
        setFormData({
            coffeeKey: '',
            fullName: '',
            iconName: availableIcons[0]?.name || '',
            isActive: true,
            coffeeFormat: defaultFormatOptions,
            rateCategories: defaultRateCategories
        });
        setEditingId(null);
        setShowAddForm(true);
    };

    const handleDuplicate = (coffee: CoffeeType) => {
        setFormData({
            coffeeKey: `${coffee.coffeeKey}-copy`,
            fullName: `${coffee.fullName} (Copy)`,
            iconName: coffee.iconName,
            isActive: coffee.isActive,
            coffeeFormat: coffee.coffeeFormat || defaultFormatOptions,
            rateCategories: coffee.rateCategories || defaultRateCategories
        });
        setEditingId(null);
        setShowAddForm(true);
    };

    const handleCancel = () => {
        setEditingId(null);
        setShowAddForm(false);
        setFormData({
            coffeeKey: '',
            fullName: '',
            iconName: '',
            isActive: true,
            coffeeFormat: {},
            rateCategories: {}
        });
    };

    const handleSave = async () => {
        try {
            const token = getAuthToken();
            if (!token) {
                toast.error('Authentication required');
                return;
            }

            if (editingId) {
                // Update existing coffee
                const response = await apiClient.put(
                    `${url}${coffeeAdminPaths.coffeeById(editingId)}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    toast.success('Coffee type updated successfully');
                    fetchCoffeeTypes();
                    handleCancel();
                }
            } else {
                // Create new coffee
                const response = await apiClient.post(
                    `${url}${coffeeAdminPaths.coffees()}`,
                    formData,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (response.data.success) {
                    toast.success('Coffee type created successfully');
                    fetchCoffeeTypes();
                    handleCancel();
                }
            }
        } catch (error: any) {
            console.error('Error saving coffee type:', error);
            toast.error(error.response?.data?.error || 'Failed to save coffee type');
        }
    };

    const handleDelete = async (coffeeId: string, hardDelete: boolean = false) => {
        const ok = await confirm({
            title: hardDelete ? "Permanently delete coffee type?" : "Deactivate coffee type?",
            message: hardDelete ? "This action cannot be undone." : "You can re-activate it later.",
            confirmLabel: hardDelete ? "Delete" : "Deactivate",
            tone: "danger",
        });
        if (!ok) return;

        try {
            const token = getAuthToken();
            const response = await apiClient.delete(
                `${url}${coffeeAdminPaths.coffeeById(coffeeId)}?hardDelete=${hardDelete}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                fetchCoffeeTypes();
            }
        } catch (error: any) {
            console.error('Error deleting coffee type:', error);
            toast.error(error.response?.data?.error || 'Failed to delete coffee type');
        }
    };

    const handleToggleActive = async (coffeeId: string) => {
        try {
            const token = getAuthToken();
            const response = await apiClient.post(
                `${url}${coffeeAdminPaths.toggle(coffeeId)}`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                toast.success(response.data.message);
                fetchCoffeeTypes();
            }
        } catch (error: any) {
            console.error('Error toggling coffee type status:', error);
            toast.error(error.response?.data?.error || 'Failed to toggle coffee type status');
        }
    };

    const addRateCategory = () => {
        const categoryName = prompt('Enter category name:');
        if (categoryName && !formData.rateCategories[categoryName]) {
            setFormData(prev => ({
                ...prev,
                rateCategories: {
                    ...prev.rateCategories,
                    [categoryName]: { weight: 5, required: false }
                }
            }));
        }
    };

    const removeRateCategory = (categoryName: string) => {
        setFormData(prev => {
            const newCategories = { ...prev.rateCategories };
            delete newCategories[categoryName];
            return {
                ...prev,
                rateCategories: newCategories
            };
        });
    };

    // small helpers for nicer labels and icons
    const formatLabel = (raw: string): string => raw.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

    const formatIcons: Record<string, IconDefinition> = {
        sugar: faCube,
        milk: faCow,
        type: faMugHot,
        'ice-cream': faIceCream
    };

    const getFormatIcon = (key: string): IconDefinition | null => formatIcons[key] || null;

    const displayedCoffees = coffeeTypes
        .filter(c => {
            const q = search.trim().toLowerCase();
            if (!q) return true;
            return (
                c.fullName.toLowerCase().includes(q) ||
                c.coffeeKey.toLowerCase().includes(q) ||
                c.iconName.toLowerCase().includes(q)
            );
        })
        .sort((a, b) => {
            // sort active first, then alphabetically by name
            if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
            return a.fullName.localeCompare(b.fullName);
        });

    if (loading) {
        return <div className="coffee-admin-loading">Loading coffee types...</div>;
    }

    return (
        <div className="coffee-admin">
            <div className="coffee-admin-header">
                <h2>
                    Coffee Types
                    <span className="count-badge">{displayedCoffees.length}</span>
                </h2>
                <div className="header-actions">
                    <div className="search-bar">
                        <FontAwesomeIcon icon={faSearch} className="search-icon" />
                        <input
                            type="text"
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <button className="add-coffee-btn" onClick={handleAdd}>
                        <FontAwesomeIcon icon={faPlus} /> Add
                    </button>
                </div>
            </div>

            {(showAddForm || editingId) && (
                <div className="coffee-form-modal">
                    <div className="coffee-form">
                        <h3>{editingId ? 'Edit Coffee Type' : 'Add New Coffee Type'}</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Coffee Key:</label>
                                <input
                                    type="text"
                                    value={formData.coffeeKey}
                                    onChange={(e) => setFormData(prev => ({ ...prev, coffeeKey: e.target.value }))}
                                    placeholder="e.g., freddo-esp"
                                />
                            </div>
                            <div className="form-group">
                                <label>Full Name:</label>
                                <input
                                    type="text"
                                    value={formData.fullName}
                                    onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                                    placeholder="e.g., Freddo Espresso"
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <IconSelector
                                    availableIcons={availableIcons}
                                    selectedIcon={formData.iconName}
                                    onIconSelect={(iconName) => setFormData(prev => ({ ...prev, iconName }))}
                                    label="Icon:"
                                />
                            </div>
                            <div className="form-group">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={formData.isActive}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                                    />
                                    Active
                                </label>
                            </div>
                        </div>

                        {/* format editor */}
                        <div className="format-editor">
                            <div className="format-editor-header">
                                <h4>Format Options</h4>
                                <button
                                    type="button"
                                    className="add-format-group-btn"
                                    onClick={() => {
                                        const key = prompt('enter new format group key (e.g., sugar, milk, type, ice-cream)');
                                        if (!key) return;
                                        const normalized = key.trim();
                                        setFormData(prev => {
                                            const next = { ...prev };
                                            const cf: any = { ...(next.coffeeFormat || {}) };
                                            if (!cf[normalized]) {
                                                cf[normalized] = {};
                                            }
                                            next.coffeeFormat = cf;
                                            return next;
                                        });
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} /> Add Group
                                </button>
                            </div>

                            <div className="format-groups">
                                {Object.entries(formData.coffeeFormat || {}).map(([groupKey, groupVal]) => (
                                    <div key={groupKey} className="format-group">
                                        <div className="format-group-header">
                                            {getFormatIcon(groupKey) && (
                                                <FontAwesomeIcon icon={getFormatIcon(groupKey)!} className="format-item-icon" />
                                            )}
                                            <span className="format-group-title">{formatLabel(groupKey)}</span>
                                            <div className="spacer" />
                                            <button
                                                type="button"
                                                className="add-subkey-btn"
                                                onClick={() => {
                                                    const subKey = prompt(`add subkey to ${groupKey} (e.g., level, type)`);
                                                    if (!subKey) return;
                                                    const normalized = subKey.trim();
                                                    setFormData(prev => {
                                                        const next = { ...prev } as any;
                                                        const cf = { ...(next.coffeeFormat || {}) } as any;
                                                        const group = { ...(cf[groupKey] || {}) } as any;
                                                        if (!group[normalized]) {
                                                            group[normalized] = [] as string[];
                                                        }
                                                        cf[groupKey] = group;
                                                        next.coffeeFormat = cf;
                                                        return next;
                                                    });
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faPlus} /> Sub-key
                                            </button>
                                            <button
                                                type="button"
                                                className="remove-group-btn"
                                                onClick={async () => {
                                                    const ok = await confirm({
                                                        title: "Remove group?",
                                                        message: `remove group ${groupKey}?`,
                                                        confirmLabel: "Remove",
                                                        tone: "danger",
                                                    });
                                                    if (!ok) return;
                                                    setFormData(prev => {
                                                        const next = { ...prev } as any;
                                                        const cf = { ...(next.coffeeFormat || {}) } as any;
                                                        delete cf[groupKey];
                                                        next.coffeeFormat = cf;
                                                        return next;
                                                    });
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faTimes} />
                                            </button>
                                        </div>

                                        <div className="format-subkeys">
                                            {typeof groupVal === 'object' && groupVal !== null ? (
                                                Object.entries(groupVal as any).map(([subKey, subVal]) => {
                                                    const keyId = `${groupKey}::${subKey}`;
                                                    const values = Array.isArray(subVal) ? (subVal as any[]) : [String(subVal)];
                                                    return (
                                                        <div className="subkey-row" key={subKey}>
                                                            <div className="subkey-title">{formatLabel(subKey)}</div>
                                                            <div className="format-chips">
                                                                {values.map((opt: any) => (
                                                                    <span key={`${keyId}-${opt}`} className="chip">
                                                                        {opt}
                                                                        <button
                                                                            type="button"
                                                                            className="chip-remove"
                                                                            onClick={() => {
                                                                                setFormData(prev => {
                                                                                    const next = { ...prev } as any;
                                                                                    const cf = { ...(next.coffeeFormat || {}) } as any;
                                                                                    const g = { ...(cf[groupKey] || {}) } as any;
                                                                                    const arr = Array.isArray(g[subKey]) ? [...g[subKey]] : [String(g[subKey])];
                                                                                    const filtered = arr.filter((v: any) => v !== opt);
                                                                                    g[subKey] = filtered;
                                                                                    cf[groupKey] = g;
                                                                                    next.coffeeFormat = cf;
                                                                                    return next;
                                                                                });
                                                                            }}
                                                                        >
                                                                            <FontAwesomeIcon icon={faTimes} />
                                                                        </button>
                                                                    </span>
                                                                ))}
                                                                <input
                                                                    type="text"
                                                                    className="chip-input"
                                                                    placeholder={`add ${formatLabel(subKey)} option`}
                                                                    value={chipInputs[keyId] || ''}
                                                                    onChange={(e) => setChipInputs(prev => ({ ...prev, [keyId]: e.target.value }))}
                                                                    onKeyDown={(e) => {
                                                                        if (e.key === 'Enter') {
                                                                            const toAdd = (chipInputs[keyId] || '').trim();
                                                                            if (!toAdd) return;
                                                                            setFormData(prev => {
                                                                                const next = { ...prev } as any;
                                                                                const cf = { ...(next.coffeeFormat || {}) } as any;
                                                                                const g = { ...(cf[groupKey] || {}) } as any;
                                                                                const arr = Array.isArray(g[subKey]) ? [...g[subKey]] : [];
                                                                                if (!arr.includes(toAdd)) arr.push(toAdd);
                                                                                g[subKey] = arr;
                                                                                cf[groupKey] = g;
                                                                                next.coffeeFormat = cf;
                                                                                return next;
                                                                            });
                                                                            setChipInputs(prev => ({ ...prev, [keyId]: '' }));
                                                                        }
                                                                    }}
                                                                />
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="remove-subkey-btn"
                                                                onClick={async () => {
                                                                    const ok = await confirm({
                                                                        title: "Remove subkey?",
                                                                        message: `remove subkey ${subKey}?`,
                                                                        confirmLabel: "Remove",
                                                                        tone: "danger",
                                                                    });
                                                                    if (!ok) return;
                                                                    setFormData(prev => {
                                                                        const next = { ...prev } as any;
                                                                        const cf = { ...(next.coffeeFormat || {}) } as any;
                                                                        const g = { ...(cf[groupKey] || {}) } as any;
                                                                        delete g[subKey];
                                                                        cf[groupKey] = g;
                                                                        next.coffeeFormat = cf;
                                                                        return next;
                                                                    });
                                                                }}
                                                            >
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <div className="empty-subkeys">no sub-keys yet</div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="rate-categories-section">
                            <h4>Rate Categories</h4>
                            <button type="button" onClick={addRateCategory} className="add-category-btn">
                                Add Category
                            </button>
                            {Object.entries(formData.rateCategories).map(([categoryName, category]) => (
                                <div key={categoryName} className="rate-category-item">
                                    <span>{categoryName}</span>
                                    <input
                                        type="number"
                                        value={category.weight}
                                        onChange={(e) => setFormData(prev => ({
                                            ...prev,
                                            rateCategories: {
                                                ...prev.rateCategories,
                                                [categoryName]: {
                                                    ...prev.rateCategories[categoryName],
                                                    weight: parseInt(e.target.value) || 0
                                                }
                                            }
                                        }))}
                                        min="1"
                                        max="10"
                                    />
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={category.required}
                                            onChange={(e) => setFormData(prev => ({
                                                ...prev,
                                                rateCategories: {
                                                    ...prev.rateCategories,
                                                    [categoryName]: {
                                                        ...prev.rateCategories[categoryName],
                                                        required: e.target.checked
                                                    }
                                                }
                                            }))}
                                        />
                                        Required
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => removeRateCategory(categoryName)}
                                        className="remove-category-btn"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </div>
                            ))}
                        </div>

                        <div className="form-actions">
                            <button onClick={handleSave} className="save-btn">
                                <FontAwesomeIcon icon={faSave} /> Save
                            </button>
                            <button onClick={handleCancel} className="cancel-btn">
                                <FontAwesomeIcon icon={faTimes} /> Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="coffee-types-list">
                {displayedCoffees.map(coffee => (
                    <div key={coffee._id} className={`coffee-type-item ${!coffee.isActive ? 'inactive' : ''}`}>
                        <div className="coffee-info">
                            <img
                                src={iconUrls[coffee.iconName] || `${url}/api/icons/icon/${coffee.iconName}`}
                                alt={coffee.displayName}
                                className="coffee-icon"
                            />
                            <div className="coffee-details">
                                <h4>{coffee.fullName}</h4>
                                <div className="coffee-meta">
                                    <span className="coffee-meta-item">{coffee.coffeeKey}</span>
                                    <span className="coffee-meta-sep" />
                                    <span className="coffee-meta-item">{coffee.iconName}</span>
                                    <span className={`status-pill ${coffee.isActive ? 'active' : 'inactive'}`}>
                                        {coffee.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                {/* Format Options Summary */}
                                {coffee.coffeeFormat && (
                                    <div className="format-summary">
                                        <div className="format-items">
                                            {Object.entries(coffee.coffeeFormat).map(([key, value]) => {
                                                const icon = getFormatIcon(key);
                                                return (
                                                    <div key={key} className="format-item">
                                                        {icon && (
                                                            <FontAwesomeIcon icon={icon} className="format-item-icon" />
                                                        )}
                                                        <span className="format-item-title">{formatLabel(key)}</span>
                                                        {typeof value === 'object' && value !== null ? (
                                                            <div className="format-chips">
                                                                {Object.entries(value).map(([subKey, subValue]) => (
                                                                    Array.isArray(subValue)
                                                                        ? subValue.map((opt: any) => (
                                                                            <span
                                                                                key={`${key}-${subKey}-${opt}`}
                                                                                className="chip"
                                                                                title={`${formatLabel(subKey)}`}
                                                                            >
                                                                                {opt}
                                                                            </span>
                                                                        ))
                                                                        : (
                                                                            <span key={`${key}-${subKey}`} className="chip">
                                                                                {`${formatLabel(subKey)}: ${String(subValue)}`}
                                                                            </span>
                                                                        )
                                                                ))}
                                                            </div>
                                                        ) : Array.isArray(value) ? (
                                                            <div className="format-chips">
                                                                {value.map((opt: any) => (
                                                                    <span key={`${key}-${opt}`} className="chip">{opt}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <span className="chip">{String(value)}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Rate Categories Summary */}
                                {coffee.rateCategories && (
                                    <div className="categories-summary">
                                        <strong>Rate Categories:</strong>
                                        <span className="categories-count">
                                            {Object.keys(coffee.rateCategories).length} categories
                                        </span>
                                        <div className="required-categories">
                                            Required: {coffee?.rateCategories ? Object.entries(coffee.rateCategories)
                                                .filter(([_, cat]) => (cat as any)?.required)
                                                .map(([name]) => name)
                                                .join(', ') : 'No categories'}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="coffee-actions">
                            <button onClick={() => handleEdit(coffee)} className="edit-btn" title="Edit">
                                <FontAwesomeIcon icon={faEdit} />
                            </button>
                            <button onClick={() => handleDuplicate(coffee)} className="duplicate-btn" title="Duplicate">
                                <FontAwesomeIcon icon={faCopy} />
                            </button>
                            <button onClick={() => handleToggleActive(coffee._id)} className="toggle-btn" title={coffee.isActive ? 'Deactivate' : 'Activate'}>
                                <FontAwesomeIcon icon={coffee.isActive ? faEyeSlash : faEye} />
                            </button>
                            <span className="action-divider" />
                            <button onClick={() => handleDelete(coffee._id, false)} className="deactivate-btn" title="Soft Delete">
                                <FontAwesomeIcon icon={faToggleOff} />
                            </button>
                            <button onClick={() => handleDelete(coffee._id, true)} className="delete-btn" title="Hard Delete">
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CoffeeAdmin; 